import { useState, useEffect } from 'react';

const STORAGE_KEY = 'verisure_env_mode';

export interface EnvironmentState {
  isProduction: boolean;
  purgeMockData: () => void;
  restoreDemoData: () => void;
}

export const isProductionMode = (): boolean => {
  if (typeof window === 'undefined') return true;
  const saved = localStorage.getItem(STORAGE_KEY);
  // Default to live production mode for real users; demo only if explicitly set
  return saved !== 'demo';
};

export const purgeAllMockData = (): void => {
  if (typeof window === 'undefined') return;

  // 1. Set mode to production
  localStorage.setItem(STORAGE_KEY, 'production');

  // 2. Clear all local mock caches
  localStorage.removeItem('local_verisure_blank_qrs');
  localStorage.removeItem('local_verisure_items');
  localStorage.removeItem('local_verisure_repairs');
  localStorage.removeItem('offline_queued_repairs');
  localStorage.removeItem('cached_technicians');
  localStorage.removeItem('verisure_blank_qrs_cache');

  // 3. Notify app components
  window.dispatchEvent(new Event('verisure_env_changed'));
};

export const restoreDemoData = (): void => {
  if (typeof window === 'undefined') return;

  localStorage.setItem(STORAGE_KEY, 'demo');
  window.dispatchEvent(new Event('verisure_env_changed'));
};

export const useEnvironmentMode = (): EnvironmentState => {
  const [isProduction, setIsProduction] = useState<boolean>(() => isProductionMode());

  useEffect(() => {
    const handleEnvChange = () => {
      setIsProduction(isProductionMode());
    };

    window.addEventListener('verisure_env_changed', handleEnvChange);
    window.addEventListener('storage', handleEnvChange);

    return () => {
      window.removeEventListener('verisure_env_changed', handleEnvChange);
      window.removeEventListener('storage', handleEnvChange);
    };
  }, []);

  return {
    isProduction,
    purgeMockData: purgeAllMockData,
    restoreDemoData,
  };
};
